import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { TRButton } from "@/components/TRButton";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
    PRICE,
    boxImage,
    packagingImage,
    styleName,
    boxName,
    getCategory,
} from "@/lib/catalog";

export default function CartPage() {
    const { items, updateQty, removeItem, total, count } = useCart();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);

    const checkout = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/checkout", {
                items: items.map((i) => ({
                    category: i.category || "socks",
                    gender: i.gender,
                    style: i.style,
                    color: i.color,
                    size: i.size,
                    boxType: i.boxType,
                    quantity: i.quantity,
                })),
                origin_url: window.location.origin,
                email: user?.email,
            });
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.error("Could not start checkout.");
            }
        } catch (e) {
            toast.error(
                e.response?.data?.detail || "Checkout failed. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="cart-page" className="pt-28 md:pt-36 min-h-[70vh]">
            <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
                <h1 className="font-serif font-light text-5xl md:text-6xl tracking-[-0.04em]">
                    Cart
                </h1>

                {count === 0 ? (
                    <div className="mt-16 border border-line py-24 text-center" data-testid="cart-empty">
                        <p className="font-serif text-2xl text-ink/70">
                            Your cart is empty.
                        </p>
                        <div className="mt-8 flex justify-center gap-4">
                            <TRButton as="link" to="/shop/socks/men">Shop Socks</TRButton>
                            <TRButton as="link" to="/shop/boxers/men" variant="outline">
                                Shop Boxers
                            </TRButton>
                        </div>
                    </div>
                ) : (
                    <div className="mt-12 grid lg:grid-cols-3 gap-12">
                        <div className="lg:col-span-2 border-t border-line">
                            <AnimatePresence initial={false}>
                                {items.map((i) => (
                                    <motion.div
                                        key={i.id}
                                        layout
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="flex gap-5 py-8 border-b border-line"
                                        data-testid={`cart-item-${i.id}`}
                                    >
                                        <div className="w-24 h-24 md:w-32 md:h-32 shrink-0 border border-line bg-secondary overflow-hidden">
                                            <img loading="lazy" decoding="async"
                                                src={packagingImage(i.category, i.color, i.gender)}
                                                alt="box"
                                                className="h-full w-full object-cover"
                                            />
                                        </div>
                                        <div className="flex-1 flex flex-col">
                                            <div className="flex justify-between items-start gap-3">
                                                <div>
                                                    <h3 className="font-serif text-2xl tracking-[-0.02em]">
                                                        {boxName(i.category, i.boxType)}
                                                    </h3>
                                                    <p className="mt-1 text-sm text-ink/55">
                                                        {[
                                                            getCategory(i.category).label,
                                                            i.gender === "men" ? "Men" : "Women",
                                                            getCategory(i.category).isBundle
                                                                ? null
                                                                : i.boxType === "starter"
                                                                  ? getCategory(i.category).assortedLabel
                                                                  : getCategory(i.category).singleModel
                                                                    ? null
                                                                    : styleName(i.category, i.style),
                                                            i.color === "black" ? "Black" : "White",
                                                            i.size,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" · ")}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => removeItem(i.id)}
                                                    data-testid={`remove-${i.id}`}
                                                    className="text-ink/40 hover:text-ink transition-colors"
                                                    aria-label="Remove"
                                                >
                                                    <X size={18} />
                                                </button>
                                            </div>
                                            <div className="mt-auto pt-4 flex items-center justify-between">
                                                <div className="inline-flex items-center border border-line">
                                                    <button
                                                        onClick={() => updateQty(i.id, i.quantity - 1)}
                                                        className="p-3 hover:bg-ink hover:text-paper transition-colors"
                                                        data-testid={`item-minus-${i.id}`}
                                                    >
                                                        <Minus size={12} />
                                                    </button>
                                                    <span className="w-10 text-center text-sm tabular-nums">
                                                        {i.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() => updateQty(i.id, i.quantity + 1)}
                                                        className="p-3 hover:bg-ink hover:text-paper transition-colors"
                                                        data-testid={`item-plus-${i.id}`}
                                                    >
                                                        <Plus size={12} />
                                                    </button>
                                                </div>
                                                <span className="font-serif text-xl">
                                                    €{PRICE * i.quantity}
                                                </span>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>

                        {/* Summary */}
                        <div className="lg:sticky lg:top-32 lg:self-start">
                            <div className="border border-line p-8">
                                <h2 className="eyebrow text-ink/50">Summary</h2>
                                <div className="mt-6 space-y-3 text-sm">
                                    <Row label={`Subtotal (${count} ${count > 1 ? "boxes" : "box"})`} value={`€${total}`} />
                                    <Row label="Shipping" value="Free" />
                                </div>
                                <div className="mt-6 pt-6 border-t border-line flex justify-between items-baseline">
                                    <span className="eyebrow text-ink/60">Total</span>
                                    <span className="font-serif text-3xl" data-testid="cart-total">
                                        €{total}
                                    </span>
                                </div>
                                <p className="mt-2 text-xs text-ink/45">
                                    Taxes calculated at checkout.
                                </p>
                                <div className="mt-8">
                                    <TRButton
                                        full
                                        onClick={checkout}
                                        disabled={loading}
                                        data-testid="checkout-btn"
                                    >
                                        {loading ? "Redirecting…" : "Secure Checkout"}
                                        {!loading && <ArrowRight size={15} strokeWidth={1.5} />}
                                    </TRButton>
                                </div>
                                <p className="mt-5 text-center text-xs text-ink/45">
                                    Free shipping included · Secure payment
                                </p>
                            </div>
                            <Link
                                to="/shop/socks/men"
                                className="mt-6 inline-block eyebrow text-ink/60 link-underline"
                            >
                                Continue shopping
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const Row = ({ label, value }) => (
    <div className="flex justify-between text-ink/70">
        <span>{label}</span>
        <span>{value}</span>
    </div>
);
